<template>
  <v-dialog :value="showUploadDialog" :fullscreen="$vuetify.breakpoint.xsOnly" max-width="760px" persistent scrollable @input="onDialogInput">
    <v-card dark class="upload-dialog-card">
      <header class="dialog-header">
        <div class="dialog-icon">
          <v-icon color="#009640">mdi-cloud-upload-outline</v-icon>
        </div>
        <div class="dialog-heading">
          <div class="dialog-eyebrow">Dokumentenablage</div>
          <h2>Dokument hochladen</h2>
          <p v-if="allowed">Datei auswählen und bei Bedarf direkt mit Rechnungsdaten ergänzen.</p>
          <p v-else>Für dein Konto ist der direkte Upload aktuell nicht freigeschaltet.</p>
        </div>
        <v-btn icon class="dialog-close" aria-label="Upload schließen" :disabled="uploading" @click="hideUploadDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </header>

      <template v-if="allowed">
        <div class="dialog-scroll">
          <v-form ref="uploadForm">
            <input ref="fileInput" type="file" class="visually-hidden" tabindex="-1" aria-hidden="true" @change="handleFileSelect" />

            <section class="upload-section">
              <div class="section-label">
                <span class="section-number">1</span>
                <div>
                  <h3>Datei</h3>
                  <p>Ziehe eine Datei hierher oder wähle sie auf deinem Gerät aus.</p>
                </div>
              </div>

              <div
                v-if="!file"
                class="drop-zone"
                role="button"
                tabindex="0"
                aria-label="Datei auswählen oder hier ablegen"
                @dragover.prevent
                @dragenter.prevent="dragActive = true"
                @dragleave.prevent="dragActive = false"
                @drop.prevent="handleDrop"
                @click="triggerFileInput"
                @keydown.enter.prevent="triggerFileInput"
                @keydown.space.prevent="triggerFileInput"
                :class="{ 'drop-zone-active': dragActive }">
                <div class="drop-zone-icon">
                  <v-icon size="34" color="#009640">mdi-tray-arrow-up</v-icon>
                </div>
                <div class="drop-zone-title">Datei hier ablegen</div>
                <div class="drop-zone-copy">oder klicken, um eine Datei auszuwählen</div>
                <div class="drop-zone-hint">Eine Datei pro Upload</div>
              </div>

              <div v-else class="selected-file">
                <div class="selected-file-icon">
                  <v-icon color="#009640">mdi-file-document-outline</v-icon>
                </div>
                <div class="selected-file-copy">
                  <strong>{{ file.name }}</strong>
                  <span>{{ formatFileSize(file.size) }}</span>
                </div>
                <v-btn icon class="touch-target" aria-label="Ausgewählte Datei entfernen" :disabled="uploading" @click="removeSelectedFile">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </div>
            </section>

            <section v-if="file" class="upload-section details-toggle-section">
              <div class="toggle-copy">
                <div class="toggle-icon">
                  <v-icon small>mdi-file-edit-outline</v-icon>
                </div>
                <div>
                  <strong>Dokument direkt erfassen</strong>
                  <span>Rechnungsdaten ergänzen, zuweisen und optional sofort visieren.</span>
                </div>
              </div>
              <v-switch
                v-if="userService.hasRight('d_fetchAllDocs')"
                v-model="assignAndSign"
                color="#009640"
                inset
                hide-details
                aria-label="Dokumentendetails direkt erfassen"></v-switch>
            </section>

            <v-expand-transition>
              <div v-if="file && assignAndSign" class="metadata-area">
                <section class="form-section">
                  <div class="section-label compact">
                    <span class="section-number">2</span>
                    <div>
                      <h3>Rechnungsdaten</h3>
                      <p>Pflichtangaben für die weitere Verarbeitung.</p>
                    </div>
                  </div>

                  <v-row>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="documentData.title"
                        label="Titel"
                        outlined
                        dense
                        hide-details="auto"
                        class="app-field"
                        :rules="[rules.required]"></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-autocomplete
                        v-model="documentData.studio"
                        :items="mappedStudios"
                        label="Studio"
                        item-text="name"
                        item-value="_id"
                        clearable
                        outlined
                        dense
                        hide-details="auto"
                        class="app-field"
                        :rules="[rules.required]"></v-autocomplete>
                    </v-col>
                    <v-col cols="12">
                      <v-textarea
                        v-model="documentData.description"
                        label="Beschreibung"
                        rows="2"
                        auto-grow
                        outlined
                        dense
                        hide-details="auto"
                        class="app-field"></v-textarea>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-autocomplete
                        v-model="selectedInvoiceIssuer"
                        return-object
                        :items="invoiceIssuerOptions"
                        label="Rechnungssteller"
                        item-text="title"
                        item-value="_id"
                        clearable
                        outlined
                        dense
                        hide-details="auto"
                        class="app-field"
                        :search-input.sync="issuerSearch"
                        :rules="[rules.required]"
                        @keydown.enter.prevent>
                        <template v-slot:no-data>
                          <v-list-item v-if="issuerSearch" class="create-option" @click="createNewInvoiceIssuer(issuerSearch)">
                            <v-list-item-icon>
                              <v-icon color="#009640">mdi-plus-circle-outline</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title>„{{ issuerSearch }}“ hinzufügen</v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                        </template>
                      </v-autocomplete>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-autocomplete
                        v-model="documentData.category"
                        :items="categories"
                        label="Kategorie"
                        clearable
                        outlined
                        dense
                        hide-details="auto"
                        class="app-field"
                        :rules="[rules.required]"></v-autocomplete>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="documentData.amount"
                        label="Betrag"
                        type="number"
                        prepend-inner-icon="mdi-cash"
                        outlined
                        dense
                        hide-details="auto"
                        class="app-field"
                        :rules="[rules.required]"></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="documentData.invoiceNumber"
                        label="Rechnungsnummer"
                        outlined
                        dense
                        hide-details="auto"
                        class="app-field"
                        :rules="[rules.required]"></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-menu ref="menu" v-model="dateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="documentData.date"
                            label="Rechnungsdatum"
                            prepend-inner-icon="mdi-calendar-outline"
                            readonly
                            outlined
                            dense
                            hide-details="auto"
                            class="app-field"
                            v-bind="attrs"
                            v-on="on"
                            :rules="[rules.required]"></v-text-field>
                        </template>
                        <v-date-picker v-model="documentData.date" color="#009640" @input="dateMenu = false"></v-date-picker>
                      </v-menu>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-menu
                        ref="menuDueDate"
                        v-model="dueDateMenu"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="auto">
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="documentData.dueDate"
                            label="Fälligkeitsdatum"
                            prepend-inner-icon="mdi-calendar-clock-outline"
                            readonly
                            outlined
                            dense
                            hide-details="auto"
                            class="app-field"
                            v-bind="attrs"
                            v-on="on"
                            :rules="[rules.required]"></v-text-field>
                        </template>
                        <v-date-picker v-model="documentData.dueDate" color="#009640" @input="dueDateMenu = false"></v-date-picker>
                      </v-menu>
                    </v-col>
                  </v-row>
                </section>

                <section class="form-section">
                  <div class="section-label compact">
                    <span class="section-number">3</span>
                    <div>
                      <h3>Zuweisung</h3>
                      <p>Lege fest, wer das Dokument bearbeiten soll.</p>
                    </div>
                  </div>

                  <user-search
                    :value="selectedUser"
                    label="Benutzer zuweisen"
                    ignoreRights
                    instructor
                    :rules="[rules.required]"
                    @update="selectedUser = $event"></user-search>

                  <div class="sign-option">
                    <div>
                      <strong>Sofort als visiert markieren</strong>
                      <span>Das Dokument erhält direkt den Status „visiert“.</span>
                    </div>
                    <v-switch
                      v-model="signImmediately"
                      color="#009640"
                      inset
                      hide-details
                      aria-label="Dokument sofort als visiert markieren"></v-switch>
                  </div>
                </section>
              </div>
            </v-expand-transition>

            <v-alert v-if="uploadSuccess" type="success" text class="app-alert"> Datei erfolgreich hochgeladen. </v-alert>
            <v-alert v-if="uploadError" type="error" text class="app-alert">
              {{ uploadError }}
            </v-alert>
          </v-form>
        </div>

        <footer class="dialog-footer">
          <v-btn text class="secondary-button" :disabled="uploading" @click="hideUploadDialog"> Abbrechen </v-btn>
          <v-btn color="#009640" depressed class="primary-button" :disabled="!file" :loading="uploading" @click="uploadAndProcessFile">
            <v-icon left small>mdi-cloud-upload-outline</v-icon>
            Hochladen
          </v-btn>
        </footer>
      </template>

      <template v-else>
        <div class="permission-state">
          <div class="permission-icon">
            <v-icon size="38" color="#009640">mdi-email-fast-outline</v-icon>
          </div>
          <h3>Direkter Upload nicht verfügbar</h3>
          <p>
            Du kannst Dokumente weiterhin über den vorgesehenen E-Mail-Weg einreichen. Falls du direkten Zugriff benötigst, wende dich bitte an eine
            berechtigte Person.
          </p>
        </div>
        <footer class="dialog-footer">
          <v-spacer></v-spacer>
          <v-btn color="#009640" depressed class="primary-button" @click="hideUploadDialog"> Verstanden </v-btn>
        </footer>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"
import userSearch from "@/components/userSearch.vue"
import { mapState } from "vuex"

export default {
  name: "FileDropUpload",
  components: { userSearch },
  props: {
    showUploadDialog: {
      type: Boolean,
      required: true,
    },
    allowed: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      rules: {
        required: (value) => !!value || "Pflichtfeld",
      },
      dueDateMenu: false,
      uploadSuccess: false,
      uploadError: "",
      uploading: false,
      dragActive: false,
      file: null,
      assignAndSign: false,
      signImmediately: false,
      selectedUser: null,
      userService,
      documentData: {
        title: "",
        description: "",
        studio: null,
        amount: null,
        dueDate: null,
        category: null,
        invoiceNumber: "",
        date: new Date().toISOString().substr(0, 10),
      },
      categories: [
        "Leistungen Dritter",
        "Personalaufwand",
        "Kreditkarten",
        "RAUMAUFWAND",
        "ENERGIE- UND ENTSORGUNGSAUFWAND",
        "SACHVERSICH., ABGABEN, GEBÜHREN, BEWILLIGUNGEN",
        "FAHRZEUG- UND TRANSPORTAUFWAND",
        "WERBEAUFWAND",
        "Reisespesen/Kundenbetreuung",
        "Reparaturen / Wartungen",
        "VERWALTUNGS-AUFWAND",
        "INFORMATIKAUFWAND",
        "Beratungskosten",
        "Lizenzen",
        "ÜBRIGER BETRIEBSAUFWAND",
        "Forderungsverluste",
        "Fitnessgeräte und Solarien",
        "Sauna / Dampfbad",
        "Handelswaren",
        "Leasing Maschinen",
        "Steuern",
        "Mobilien und Einrichtungen",
        "Nicht Gewinnwirksame Aufwände",
        "Büromobiliar/Büro-/EDV-Geräte",
        "Fahrzeuge",
        "Unbekannt",
      ],
      selectedInvoiceIssuer: null,
      invoiceIssuerOptions: [],
      issuerSearch: "",
      dateMenu: false,
    }
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),
    mappedStudios() {
      return this.storeStudios
        .filter((studio) => studio.code && studio.code !== "ot" && studio.code !== "alibey")
        .map((studio) => ({ _id: studio._id, name: studio.name }))
    },
  },
  methods: {
    onDialogInput(value) {
      if (!value) this.hideUploadDialog()
    },
    triggerFileInput() {
      if (!this.uploading && this.$refs.fileInput) this.$refs.fileInput.click()
    },
    handleFileSelect(event) {
      const files = event.target.files
      if (files.length > 0) this.setSelectedFile(files[0])
    },
    setSelectedFile(file) {
      this.file = file
      this.dragActive = false
      this.documentData.title = file.name.replace(/\.[^/.]+$/, "")
    },
    removeSelectedFile() {
      this.file = null
      if (this.$refs.fileInput) this.$refs.fileInput.value = ""
    },
    formatFileSize(size) {
      if (typeof size !== "number") return ""
      if (size < 1024) return `${size} B`
      if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`
      return `${(size / (1024 * 1024)).toFixed(1)} MB`
    },
    resetForm() {
      const currentUser = this.userService.getUser()
      this.file = null
      this.assignAndSign = false
      this.signImmediately = false
      this.selectedUser = currentUser && currentUser._id ? currentUser._id : null
      this.dragActive = false
      this.documentData = {
        title: "",
        description: "",
        studio: null,
        category: null,
        dueDate: null,
        amount: null,
        invoiceNumber: "",
        date: new Date().toISOString().substr(0, 10),
      }
      this.selectedInvoiceIssuer = null
      this.issuerSearch = ""
      if (this.$refs.fileInput) this.$refs.fileInput.value = ""
    },
    hideUploadDialog() {
      if (this.uploading) return
      this.resetForm()
      this.uploadSuccess = false
      this.uploadError = ""
      this.$emit("hideDialog")
    },
    handleDrop(event) {
      this.dragActive = false
      const files = event.dataTransfer.files
      if (files.length > 0) this.setSelectedFile(files[0])
    },
    async fetchInvoiceIssuers() {
      try {
        this.invoiceIssuerOptions = await api.invoiceIssuers.fetch()
      } catch (err) {
        console.error("Fehler beim Abrufen der Rechnungssteller:", err)
      }
    },
    createNewInvoiceIssuer(title) {
      api.invoiceIssuers.create({ title }).then((createdIssuer) => {
        this.invoiceIssuerOptions.push(createdIssuer)
        this.selectedInvoiceIssuer = createdIssuer
        this.issuerSearch = ""
      })
    },
    async uploadAndProcessFile() {
      if (!this.file || this.uploading) return
      if (this.$refs.uploadForm && !this.$refs.uploadForm.validate()) return

      this.uploading = true
      try {
        const formData = new FormData()
        formData.append("file", this.file)

        if (this.allowed && this.assignAndSign) {
          formData.append("assignedUserId", this.selectedUser)
          formData.append("sign", this.signImmediately)
          Object.keys(this.documentData).forEach((key) => {
            if (this.documentData[key] !== null) formData.append(key, this.documentData[key])
          })
          if (this.selectedInvoiceIssuer) formData.append("invoiceIssuer", this.selectedInvoiceIssuer._id)
        }

        await api.documents.uploadDocument(formData)
        this.uploadSuccess = true
        this.uploadError = ""
        this.$emit("fileUpload")
        this.uploading = false
        this.hideUploadDialog()
      } catch (error) {
        this.uploadSuccess = false
        this.uploadError = "Fehler beim Hochladen der Datei."
        console.error("Upload error:", error)
      } finally {
        this.uploading = false
      }
    },
  },
  watch: {
    showUploadDialog(newVal) {
      if (newVal) {
        this.resetForm()
        this.fetchInvoiceIssuers()
      }
    },
  },
}
</script>

<style scoped>
.upload-dialog-card {
  --dialog-bg: rgba(28, 28, 29, 0.97);
  --dialog-surface: rgba(255, 255, 255, 0.045);
  --dialog-surface-soft: rgba(255, 255, 255, 0.065);
  --dialog-border: rgba(255, 255, 255, 0.09);
  --dialog-border-strong: rgba(255, 255, 255, 0.15);
  --dialog-text: #f5f5f5;
  --dialog-muted: #a3a3a3;
  display: flex;
  max-height: min(90vh, 920px);
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--dialog-border-strong);
  border-radius: 22px !important;
  background: var(--dialog-bg) !important;
  backdrop-filter: blur(22px);
  box-shadow: 0 30px 90px rgba(0, 0, 0, 0.46);
}

.dialog-header {
  display: flex;
  flex: 0 0 auto;
  align-items: flex-start;
  gap: 14px;
  padding: 24px 24px 20px;
  border-bottom: 1px solid var(--dialog-border);
}

.dialog-icon {
  display: grid;
  flex: 0 0 48px;
  width: 48px;
  height: 48px;
  place-items: center;
  border: 1px solid rgba(0, 150, 64, 0.25);
  border-radius: 16px;
  background: rgba(0, 150, 64, 0.11);
}

.dialog-heading {
  min-width: 0;
  flex: 1;
}

.dialog-eyebrow {
  margin-bottom: 3px;
  color: #65c98f;
  font-size: 0.67rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.dialog-heading h2 {
  margin: 0;
  color: var(--dialog-text);
  font-size: 1.23rem;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.dialog-heading p {
  margin: 6px 0 0;
  color: var(--dialog-muted);
  font-size: 0.82rem;
  line-height: 1.5;
}

.dialog-close,
.touch-target {
  min-width: 44px !important;
  min-height: 44px !important;
}

.dialog-close {
  margin-top: -4px;
  margin-right: -8px;
}

.dialog-scroll {
  min-height: 0;
  padding: 20px 24px 26px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.upload-section,
.form-section {
  margin-bottom: 16px;
  padding: 18px;
  border: 1px solid var(--dialog-border);
  border-radius: 19px;
  background: var(--dialog-surface);
}

.section-label {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
}

.section-label.compact {
  margin-bottom: 10px;
}

.section-number {
  display: grid;
  flex: 0 0 30px;
  width: 30px;
  height: 30px;
  place-items: center;
  border-radius: 10px;
  background: rgba(0, 150, 64, 0.14);
  color: #7ad69f;
  font-size: 0.75rem;
  font-weight: 800;
}

.section-label h3 {
  margin: 1px 0 0;
  color: var(--dialog-text);
  font-size: 0.92rem;
  font-weight: 750;
}

.section-label p {
  margin: 3px 0 0;
  color: var(--dialog-muted);
  font-size: 0.76rem;
  line-height: 1.45;
}

.drop-zone {
  display: flex;
  min-height: 224px;
  padding: 28px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1.5px dashed rgba(255, 255, 255, 0.2);
  border-radius: 17px;
  outline: none;
  background: rgba(255, 255, 255, 0.025);
  cursor: pointer;
  text-align: center;
  transition: border-color 160ms ease, background 160ms ease, transform 160ms ease;
}

.drop-zone:hover,
.drop-zone:focus-visible,
.drop-zone-active {
  border-color: #009640;
  background: rgba(0, 150, 64, 0.08);
  transform: translateY(-1px);
}

.drop-zone-icon {
  display: grid;
  width: 64px;
  height: 64px;
  margin-bottom: 15px;
  place-items: center;
  border-radius: 19px;
  background: rgba(0, 150, 64, 0.12);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.2);
}

.drop-zone-title {
  color: var(--dialog-text);
  font-size: 0.98rem;
  font-weight: 800;
}

.drop-zone-copy {
  margin-top: 5px;
  color: var(--dialog-muted);
  font-size: 0.8rem;
}

.drop-zone-hint {
  margin-top: 14px;
  padding: 5px 9px;
  border: 1px solid var(--dialog-border);
  border-radius: 8px;
  color: #65c98f;
  font-size: 0.68rem;
  font-weight: 700;
}

.selected-file {
  display: flex;
  min-height: 74px;
  align-items: center;
  gap: 13px;
  padding: 12px 13px;
  border: 1px solid rgba(0, 150, 64, 0.22);
  border-radius: 16px;
  background: rgba(0, 150, 64, 0.07);
}

.selected-file-icon,
.toggle-icon {
  display: grid;
  flex: 0 0 42px;
  width: 42px;
  height: 42px;
  place-items: center;
  border-radius: 13px;
  background: rgba(0, 150, 64, 0.12);
}

.selected-file-copy {
  min-width: 0;
  flex: 1;
}

.selected-file-copy strong,
.selected-file-copy span {
  display: block;
}

.selected-file-copy strong {
  overflow: hidden;
  color: var(--dialog-text);
  font-size: 0.84rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.selected-file-copy span {
  margin-top: 3px;
  color: var(--dialog-muted);
  font-size: 0.7rem;
}

.details-toggle-section,
.sign-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.toggle-copy {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 12px;
}

.toggle-copy strong,
.toggle-copy span,
.sign-option strong,
.sign-option span {
  display: block;
}

.toggle-copy strong,
.sign-option strong {
  color: var(--dialog-text);
  font-size: 0.83rem;
}

.toggle-copy span,
.sign-option span {
  margin-top: 3px;
  color: var(--dialog-muted);
  font-size: 0.72rem;
  line-height: 1.45;
}

.metadata-area {
  padding-top: 2px;
}

.form-section ::v-deep .row {
  margin: -6px;
}

.form-section ::v-deep .col,
.form-section ::v-deep [class*="col-"] {
  padding: 6px;
}

.app-field ::v-deep .v-input__slot {
  min-height: 46px !important;
  border-radius: 13px !important;
  background: rgba(255, 255, 255, 0.035) !important;
}

.app-field ::v-deep fieldset {
  border-color: var(--dialog-border-strong) !important;
}

.app-field.v-input--is-focused ::v-deep fieldset {
  border-color: #009640 !important;
}

.app-field.v-input--is-focused ::v-deep .v-label,
.app-field.v-input--is-focused ::v-deep .v-icon {
  color: #65c98f !important;
}

.upload-dialog-card ::v-deep .primary--text {
  color: #009640 !important;
  caret-color: #009640 !important;
}

.sign-option {
  margin-top: 14px;
  padding: 13px 14px;
  border: 1px solid var(--dialog-border);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.03);
}

.app-alert {
  border-radius: 15px;
}

.dialog-footer {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  min-height: 76px;
  padding: 14px 24px calc(14px + env(safe-area-inset-bottom));
  border-top: 1px solid var(--dialog-border);
  background: rgba(22, 22, 23, 0.96);
  box-shadow: 0 -16px 34px rgba(0, 0, 0, 0.18);
}

.primary-button,
.secondary-button {
  min-height: 44px !important;
  border-radius: 13px !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.primary-button:not(.v-btn--disabled) {
  background-color: #009640 !important;
  color: #fff !important;
}

.permission-state {
  display: flex;
  min-height: 330px;
  padding: 44px 32px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.permission-icon {
  display: grid;
  width: 72px;
  height: 72px;
  margin-bottom: 18px;
  place-items: center;
  border: 1px solid rgba(0, 150, 64, 0.24);
  border-radius: 22px;
  background: rgba(0, 150, 64, 0.1);
}

.permission-state h3 {
  margin: 0;
  color: var(--dialog-text);
  font-size: 1.08rem;
  font-weight: 800;
}

.permission-state p {
  max-width: 470px;
  margin: 10px 0 0;
  color: var(--dialog-muted);
  font-size: 0.83rem;
  line-height: 1.65;
}

.visually-hidden {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  border: 0 !important;
  white-space: nowrap !important;
}

@media (max-width: 599px) {
  .upload-dialog-card {
    max-height: 100%;
    border: 0;
    border-radius: 0 !important;
  }

  .dialog-header {
    padding: 18px 16px 16px;
  }

  .dialog-icon {
    flex-basis: 44px;
    width: 44px;
    height: 44px;
    border-radius: 14px;
  }

  .dialog-heading h2 {
    font-size: 1.05rem;
  }

  .dialog-heading p {
    display: none;
  }

  .dialog-scroll {
    padding: 14px 14px 20px;
  }

  .upload-section,
  .form-section {
    padding: 15px;
    border-radius: 17px;
  }

  .drop-zone {
    min-height: 200px;
    padding: 22px 16px;
  }

  .details-toggle-section {
    align-items: flex-start;
  }

  .toggle-icon {
    display: none;
  }

  .dialog-footer {
    min-height: 72px;
    padding: 12px 14px calc(12px + env(safe-area-inset-bottom));
  }

  .dialog-footer .v-btn {
    min-width: 0 !important;
  }

  .permission-state {
    min-height: 0;
    flex: 1;
    padding: 36px 24px;
  }
}
</style>
